import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { toast } from "sonner";
import { ShieldAlert, Mail, MessageCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [disabled, setDisabled] = useState(null); // { reason, email }
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setDisabled(null);
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      const to = location.state?.from || (res.user.role === "admin" ? "/admin" : "/dashboard");
      navigate(to);
    } else if (res.disabled) {
      // Friendly "account disabled" screen — not a toast, this needs attention.
      setDisabled({ reason: res.disabledReason, email });
    } else {
      setError(res.error);
    }
  };

  if (disabled) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto" data-testid="login-disabled-screen">
          <div className="bg-white border border-brand-linen rounded-3xl shadow-card overflow-hidden">
            <div className="bg-brand-accent/10 border-b border-brand-accent/20 px-6 sm:px-8 py-6">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 rounded-full p-2.5 bg-brand-accent text-white">
                  <ShieldAlert className="w-5 h-5" strokeWidth={1.7} />
                </span>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-brand-accent/80 font-medium">
                    Account suspended
                  </div>
                  <h1 className="font-serif text-2xl sm:text-3xl text-brand-ink mt-1">
                    This account has been disabled.
                  </h1>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-5">
              <p className="text-brand-body">
                The administrator has temporarily blocked
                {" "}<span className="font-medium text-brand-ink">{disabled.email}</span>
                {" "}from signing in. Your training record is kept safe — it isn't deleted.
              </p>

              {disabled.reason && (
                <div className="rounded-2xl border border-brand-linen bg-brand-bone/50 p-4" data-testid="login-disabled-reason">
                  <div className="text-xs uppercase tracking-widest text-brand-muted mb-1">Reason given</div>
                  <div className="text-brand-ink text-sm">{disabled.reason}</div>
                </div>
              )}

              <div>
                <div className="font-medium text-brand-ink mb-2">What to do next</div>
                <ol className="text-sm text-brand-body list-decimal list-inside space-y-1.5">
                  <li>If you work at a GP practice on Calside, speak to your Practice Manager — they can confirm the status of your account.</li>
                  <li>If you believe this is a mistake, contact the Calside administrator directly using the options below.</li>
                  <li>Once the admin re-enables your account you can sign in here again with the same email and password.</li>
                </ol>
              </div>

              <div className="rounded-2xl border border-brand-linen p-4">
                <div className="text-xs uppercase tracking-widest text-brand-muted mb-3">Contact the administrator</div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="mailto:Grant.Anthony2@nhs.scot?subject=Calside%20account%20suspended"
                    data-testid="login-disabled-email"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-ink text-brand-bone px-5 py-2.5 text-sm hover:bg-brand-inkHover"
                  >
                    <Mail className="w-4 h-4" strokeWidth={1.7} /> Grant.Anthony2@nhs.scot
                  </a>
                  <a
                    href="https://wa.me/447590335390"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="login-disabled-whatsapp"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-linen px-5 py-2.5 text-sm hover:bg-brand-bone"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={1.7} /> WhatsApp · 07590 335390
                  </a>
                </div>
              </div>

              <button
                type="button"
                data-testid="login-disabled-back"
                onClick={() => {
                  setDisabled(null);
                  setError("");
                  setPassword("");
                }}
                className="text-sm text-brand-muted hover:text-brand-ink underline underline-offset-4"
              >
                ← Back to sign in
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="grid lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-4">Sign in</div>
          <h1 className="font-serif text-5xl text-brand-ink leading-tight">Welcome back to the surgery.</h1>
          <p className="mt-4 text-brand-body">Pick up exactly where you left off — same reading tree, same coffee.</p>
        </div>
        <form onSubmit={onSubmit} className="bg-white border border-brand-linen rounded-3xl p-8 shadow-card space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Email</label>
            <input data-testid={T.emailInput} type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-brand-muted">Password</label>
            <input data-testid={T.passwordInput} type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-2 px-4 py-3 border border-brand-linen rounded-xl bg-brand-bone focus:outline-none focus:border-brand-ink transition-colors" />
          </div>
          {error && <div data-testid={T.authError} className="text-sm text-brand-accent bg-brand-accent/10 border border-brand-accent/20 rounded-xl px-4 py-3">{error}</div>}
          <button data-testid={T.loginSubmit} disabled={busy} type="submit"
            className="w-full bg-brand-ink text-brand-bone rounded-full py-3 font-medium hover:bg-brand-inkHover disabled:opacity-60 transition-colors">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-sm text-brand-muted text-center">
            New here? <Link to="/register" className="text-brand-ink underline underline-offset-4">Create an account</Link>
          </div>
        </form>
      </div>
    </Layout>
  );
}
